import React, { useState, useEffect } from 'react';
import { Bell, Trash2, Plus, TrendingUp, Moon, BarChart2 } from 'lucide-react';
import InputCard from '../common/InputCard';
import TipCard from '../common/TipCard';
import { motion, AnimatePresence } from 'framer-motion';

const NotificationOverloadDetector = () => {
  const [apps, setApps] = useState([
    { id: 1, name: "Instagram", notifications: 45, lateNight: true },
    { id: 2, name: "WhatsApp", notifications: 120, lateNight: false },
    { id: 3, name: "Twitter/X", notifications: 30, lateNight: true },
    { id: 4, name: "Email", notifications: 85, lateNight: false },
  ]);
  const [newAppName, setNewAppName] = useState("");
  const [totalScore, setTotalScore] = useState(0);
  const [rankedApps, setRankedApps] = useState([]);

  useEffect(() => {
    const scored = apps.map(app => {
      let score = Math.min(50, app.notifications * 0.4);
      if (app.lateNight) score += Math.min(30, app.notifications * 0.25);
      if (app.notifications > 100) score += 15;
      else if (app.notifications > 50) score += 8;
      else if (app.notifications > 30) score += 4;
      return { ...app, score: Math.min(100, Math.round(score)) };
    });
    const sorted = [...scored].sort((a, b) => b.score - a.score);
    setRankedApps(sorted);
    const total = Math.min(100, Math.round(scored.reduce((sum, app) => sum + app.score, 0) / Math.max(1, apps.length)));
    setTotalScore(total);
  }, [apps]);

  const addApp = () => {
    if (newAppName.trim()) {
      setApps([...apps, { id: Date.now(), name: newAppName, notifications: 0, lateNight: false }]);
      setNewAppName("");
    }
  };
  
  const removeApp = (id) => setApps(apps.filter(app => app.id !== id));
  
  const updateApp = (id, field, value) => {
    setApps(apps.map(app => app.id === id ? { ...app, [field]: value } : app));
  };

  const getScoreColor = (score) => {
    if (score < 30) return "bg-green-100 text-green-700";
    if (score < 60) return "bg-yellow-100 text-yellow-700";
    return "bg-red-100 text-red-700";
  };

  const getDistractionLevel = () => {
    if (totalScore < 30) return { level: "Low", color: "text-green-600", emoji: "😊" };
    if (totalScore < 60) return { level: "Medium", color: "text-yellow-600", emoji: "😐" };
    return { level: "High", color: "text-red-600", emoji: "😫" };
  };

  const distraction = getDistractionLevel();

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <InputCard title="App Notification Tracker" icon={Bell} delay={0.1}>
          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            <AnimatePresence>
              {apps.map((app, idx) => (
                <motion.div 
                  key={app.id} 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl"
                >
                  <span className="text-xs font-medium text-gray-400 w-6">{idx+1}</span>
                  <input 
                    type="text" 
                    value={app.name} 
                    onChange={(e) => updateApp(app.id, "name", e.target.value)} 
                    className="flex-1 px-3 py-2 border rounded-lg text-sm font-medium"
                  />
                  <input 
                    type="number" 
                    value={app.notifications} 
                    onChange={(e) => updateApp(app.id, "notifications", parseInt(e.target.value) || 0)} 
                    className="w-20 px-2 py-2 border rounded-lg text-sm text-center"
                    placeholder="Daily"
                  />
                  <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                    <input 
                      type="checkbox" 
                      checked={app.lateNight} 
                      onChange={(e) => updateApp(app.id, "lateNight", e.target.checked)} 
                      className="mr-1"
                    /> 
                    <Moon size={12} /> Night
                  </label>
                  <button 
                    onClick={() => removeApp(app.id)} 
                    className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} className="text-red-400 hover:text-red-600" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
            <div className="flex gap-2 pt-3">
              <input 
                type="text" 
                value={newAppName} 
                onChange={(e) => setNewAppName(e.target.value)} 
                placeholder="App name" 
                className="flex-1 px-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && addApp()}
              />
              <button 
                onClick={addApp} 
                className="px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all flex items-center gap-2"
              >
                <Plus size={18} /> Add
              </button>
            </div>
          </div>
        </InputCard>

        <div className="space-y-5">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm p-6 border text-center"
          >
            <div className="text-7xl mb-2">{distraction.emoji}</div>
            <div className={`text-6xl font-bold mb-2 ${distraction.color}`}>{totalScore}</div>
            <p className="text-gray-500 mb-2">Total Distraction Score</p>
            <div className={`inline-block px-4 py-1 rounded-full text-lg font-semibold ${getScoreColor(totalScore)}`}>
              {distraction.level} Distraction Level
            </div>
            <div className="mt-4">
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-700 ${totalScore >= 60 ? "bg-red-500" : totalScore >= 30 ? "bg-yellow-500" : "bg-green-500"}`}
                  style={{ width: `${totalScore}%` }}
                />
              </div>
            </div>
          </motion.div>

          <div className="bg-white rounded-xl shadow-sm p-5 border">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <TrendingUp size={18} className="text-orange-500" />
              Most Distracting Apps
            </h3>
            <div className="space-y-2">
              {rankedApps.map((app, idx) => (
                <div key={app.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-gray-400 w-6">#{idx+1}</span>
                    <span className="font-medium">{app.name}</span>
                    {app.lateNight && <Moon size={12} className="text-indigo-400" />}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">{app.notifications}/day</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getScoreColor(app.score)}`}>
                      {app.score}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TipCard icon={Moon} title="Reduce Night Notifications" text="Mute notifications after 10 PM for better sleep" color="purple" />
        <TipCard icon={Bell} title="Batch Processing" text="Check notifications at set times, not constantly" color="blue" />
        <TipCard icon={BarChart2} title="Weekly Audit" text="Review which apps you actually need notifications from" color="green" />
      </div>
    </motion.div>
  );
};

export default NotificationOverloadDetector;